import React, { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  Send,
  KeyRound,
  Pencil,
  Copy,
  Check,
  Loader2,
  Webhook,
  Power,
  PowerOff,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Switch } from '../components/ui/Switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/Dialog';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { useToast } from '../components/ui/Toast';
import {
  useWebhooksApi,
  WebhookSubscription,
  WEBHOOK_EVENT_TYPES,
} from '../apis/webhooks-apis';

const EVENT_TYPE_LABELS: Record<string, string> = {
  'playbook.created': 'Created',
  'playbook.updated': 'Updated',
  'playbook.deleted': 'Deleted',
  'playbook.published': 'Published',
  'playbook.rolled_back': 'Rolled Back',
};

const WebhooksPage: React.FC = () => {
  const webhooksApi = useWebhooksApi();
  const { addToast } = useToast();

  const [subscriptions, setSubscriptions] = useState<WebhookSubscription[]>([]);
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    targetUrl: '',
    description: '',
    eventTypes: ['*'] as string[],
    allEvents: true,
  });
  const [creating, setCreating] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editSub, setEditSub] = useState<WebhookSubscription | null>(null);
  const [editForm, setEditForm] = useState({
    targetUrl: '',
    description: '',
    eventTypes: [] as string[],
    isActive: true,
    allEvents: true,
  });
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSub, setDeleteSub] = useState<WebhookSubscription | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Secret display
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [secretDialogOpen, setSecretDialogOpen] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  // Action loading states
  const [testingId, setTestingId] = useState<string | null>(null);
  const [rotatingId, setRotatingId] = useState<string | null>(null);

  const fetchSubscriptions = async () => {
    try {
      const data = await webhooksApi.listSubscriptions();
      setSubscriptions(data);
    } catch (err) {
      addToast({
        title: 'Error',
        description: 'Failed to load webhook subscriptions',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Create ---
  const handleCreate = async () => {
    const url = createForm.targetUrl.trim();
    if (!url) {
      addToast({ title: 'Validation Error', description: 'Target URL is required', variant: 'destructive' });
      return;
    }
    if (!url.startsWith('https://') && !url.startsWith('http://localhost')) {
      addToast({ title: 'Validation Error', description: 'Target URL must start with https://', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      const eventTypes = createForm.allEvents
        ? ['*']
        : createForm.eventTypes;
      if (!createForm.allEvents && eventTypes.length === 0) {
        addToast({ title: 'Validation Error', description: 'Select at least one event type', variant: 'destructive' });
        setCreating(false);
        return;
      }
      const result = await webhooksApi.createSubscription({
        targetUrl: createForm.targetUrl.trim(),
        eventTypes,
        description: createForm.description.trim(),
      });
      setCreateOpen(false);
      setCreateForm({ targetUrl: '', description: '', eventTypes: ['*'], allEvents: true });
      // Show the secret
      setRevealedSecret(result.secret);
      setSecretDialogOpen(true);
      await fetchSubscriptions();
      addToast({ title: 'Success', description: 'Webhook subscription created' });
    } catch (err: any) {
      addToast({ title: 'Error', description: err.message || 'Failed to create subscription', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  // --- Edit ---
  const openEdit = (sub: WebhookSubscription) => {
    const isAll = sub.eventTypes.includes('*');
    setEditSub(sub);
    setEditForm({
      targetUrl: sub.targetUrl,
      description: sub.description,
      eventTypes: isAll ? [...WEBHOOK_EVENT_TYPES] : sub.eventTypes,
      isActive: sub.isActive,
      allEvents: isAll,
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editSub) return;
    setSaving(true);
    try {
      const eventTypes = editForm.allEvents
        ? ['*']
        : editForm.eventTypes;
      await webhooksApi.updateSubscription(editSub.subscriptionId, {
        targetUrl: editForm.targetUrl.trim(),
        description: editForm.description.trim(),
        eventTypes,
        isActive: editForm.isActive,
      });
      setEditOpen(false);
      await fetchSubscriptions();
      addToast({ title: 'Success', description: 'Subscription updated' });
    } catch (err: any) {
      addToast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // --- Delete ---
  const handleDelete = async () => {
    if (!deleteSub) return;
    setDeleting(true);
    try {
      await webhooksApi.deleteSubscription(deleteSub.subscriptionId);
      setDeleteOpen(false);
      setDeleteSub(null);
      await fetchSubscriptions();
      addToast({ title: 'Success', description: 'Subscription deleted' });
    } catch (err: any) {
      addToast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  // --- Test ping ---
  const handleTest = async (subId: string) => {
    setTestingId(subId);
    try {
      await webhooksApi.testSubscription(subId);
      addToast({ title: 'Success', description: 'Ping event sent' });
    } catch (err: any) {
      addToast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setTestingId(null);
    }
  };

  // --- Rotate secret ---
  const handleRotate = async (subId: string) => {
    setRotatingId(subId);
    try {
      const result = await webhooksApi.rotateSecret(subId);
      setRevealedSecret(result.secret);
      setSecretDialogOpen(true);
      addToast({ title: 'Success', description: 'Secret rotated' });
    } catch (err: any) {
      addToast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setRotatingId(null);
    }
  };

  // --- Copy secret ---
  const handleCopySecret = async () => {
    if (!revealedSecret) return;
    await navigator.clipboard.writeText(revealedSecret);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  // --- Toggle event type ---
  const toggleEventType = (
    eventType: string,
    form: typeof createForm | typeof editForm,
    setForm: (f: any) => void
  ) => {
    const types = form.eventTypes.includes(eventType)
      ? form.eventTypes.filter((t: string) => t !== eventType)
      : [...form.eventTypes, eventType];
    setForm({ ...form, eventTypes: types });
  };

  // --- Quick toggle active ---
  const handleToggleActive = async (sub: WebhookSubscription) => {
    try {
      await webhooksApi.updateSubscription(sub.subscriptionId, {
        isActive: !sub.isActive,
      });
      await fetchSubscriptions();
      addToast({
        title: 'Success',
        description: `Subscription ${sub.isActive ? 'disabled' : 'enabled'}`,
      });
    } catch (err: any) {
      addToast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  // --- Event type picker component ---
  const EventTypePicker = ({
    form,
    setForm,
  }: {
    form: typeof createForm | typeof editForm;
    setForm: (f: any) => void;
  }) => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Switch
          checked={form.allEvents}
          onCheckedChange={(checked) =>
            setForm({
              ...form,
              allEvents: checked,
              eventTypes: checked ? ['*'] : [...WEBHOOK_EVENT_TYPES],
            })
          }
        />
        <Label className="text-sm">All events</Label>
      </div>
      {!form.allEvents && (
        <div className="flex flex-wrap gap-2">
          {WEBHOOK_EVENT_TYPES.map((et) => (
            <button
              key={et}
              type="button"
              onClick={() => toggleEventType(et, form, setForm)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                form.eventTypes.includes(et)
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:border-primary/50'
              }`}
            >
              {EVENT_TYPE_LABELS[et] || et}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Webhook className="h-6 w-6" />
            Webhook Subscriptions
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage outbound webhook notifications for playbook changes
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Subscription
        </Button>
      </div>

      {/* Empty state */}
      {subscriptions.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Webhook className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-1">No webhook subscriptions</p>
            <p className="text-sm text-muted-foreground mb-4">
              Create a subscription to receive notifications when playbooks change.
            </p>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Subscription
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Subscriptions list */}
      <div className="space-y-4">
        {subscriptions.map((sub) => (
          <Card key={sub.subscriptionId}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4">
                {/* Left: Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="font-mono text-sm truncate max-w-md"
                      title={sub.targetUrl}
                    >
                      {sub.targetUrl}
                    </span>
                    <Badge variant={sub.isActive ? 'default' : 'secondary'}>
                      {sub.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  {sub.description && (
                    <p className="text-sm text-muted-foreground mb-2">
                      {sub.description}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {sub.eventTypes.includes('*') ? (
                      <Badge variant="outline" className="text-xs">All Events</Badge>
                    ) : (
                      sub.eventTypes.map((et) => (
                        <Badge key={et} variant="outline" className="text-xs">
                          {EVENT_TYPE_LABELS[et] || et}
                        </Badge>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    ID: {sub.subscriptionId} | Created by: {sub.createdByEmail}
                  </p>
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    title={sub.isActive ? 'Disable' : 'Enable'}
                    onClick={() => handleToggleActive(sub)}
                  >
                    {sub.isActive ? (
                      <PowerOff className="h-4 w-4" />
                    ) : (
                      <Power className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Send test ping"
                    onClick={() => handleTest(sub.subscriptionId)}
                    disabled={testingId === sub.subscriptionId}
                  >
                    {testingId === sub.subscriptionId ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Rotate secret"
                    onClick={() => handleRotate(sub.subscriptionId)}
                    disabled={rotatingId === sub.subscriptionId}
                  >
                    {rotatingId === sub.subscriptionId ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <KeyRound className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Edit"
                    onClick={() => openEdit(sub)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Delete"
                    onClick={() => {
                      setDeleteSub(sub);
                      setDeleteOpen(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Webhook Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Target URL</Label>
              <Input
                placeholder="https://example.com/webhook"
                value={createForm.targetUrl}
                onChange={(e) =>
                  setCreateForm({ ...createForm, targetUrl: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Optional description"
                value={createForm.description}
                onChange={(e) =>
                  setCreateForm({ ...createForm, description: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Event Types</Label>
              <EventTypePicker form={createForm} setForm={setCreateForm} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Target URL</Label>
              <Input
                value={editForm.targetUrl}
                onChange={(e) =>
                  setEditForm({ ...editForm, targetUrl: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={editForm.description}
                onChange={(e) =>
                  setEditForm({ ...editForm, description: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Event Types</Label>
              <EventTypePicker form={editForm} setForm={setEditForm} />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={editForm.isActive}
                onCheckedChange={(checked) =>
                  setEditForm({ ...editForm, isActive: checked })
                }
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
        title="Delete Subscription"
        description={`This will permanently delete the webhook subscription to ${deleteSub?.targetUrl}. No further events will be delivered.`}
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        disabled={deleting}
        requireVerification
        verificationWord="DELETE"
      />

      {/* Secret Reveal Dialog */}
      <Dialog
        open={secretDialogOpen}
        onOpenChange={(open) => {
          setSecretDialogOpen(open);
          if (!open) {
            setRevealedSecret(null);
            setCopiedSecret(false);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Webhook Signing Secret</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              This secret is shown only once. Copy and store it securely. Use it
              to verify webhook signatures via HMAC-SHA256.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-muted p-3 rounded text-sm font-mono break-all">
                {revealedSecret}
              </code>
              <Button variant="outline" size="icon" onClick={handleCopySecret}>
                {copiedSecret ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setSecretDialogOpen(false);
                setRevealedSecret(null);
                setCopiedSecret(false);
              }}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WebhooksPage;
